import { useRef } from 'react';
import { TimelineRuler } from './TimelineRuler';
import { TimelineCursor } from './TimelineCursor';
import { TimelineAnnotations } from './TimelineAnnotations';
import { TimelinePreview } from './TimelinePreview';
import { AnimatePresence } from 'framer-motion';
import { Annotation } from '../../../types/annotation';
import { useTimelineControls } from '../useTimeControls';
import { TimelineMinimap } from './TimelineMiniMap';
import { formatTime } from '../../utilities/time';

interface AnnotationTimelineProps {
  duration: number;
  currentTime: number;
  annotations: Annotation[];
  streamStart?: Date;
  streamEnd?: Date;
  previewStartTime?: number;
  previewEndTime?: number;
  onSeek: (time: number) => void;
  onAnnotationClick: (id: string) => void;
}

function formatStreamTime(time: Date) {
  return time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
}

export function AnnotationTimeline({
  duration,
  currentTime,
  streamStart,
  streamEnd,
  annotations,
  previewStartTime,
  previewEndTime,
  onSeek,
  onAnnotationClick,
}: AnnotationTimelineProps) {
  const timelineRef = useRef<HTMLDivElement>(null);
  const { 
    handleTimelineClick,
    handleTimelineMouseMove
  } = useTimelineControls(timelineRef, duration, onSeek);

  return (
    <div className="tw-w-full tw-space-y-2">
      <div className="tw-h-6">
        <TimelineMinimap
          duration={duration}
          currentTime={currentTime}
          annotations={annotations}
          streamStart={streamStart}
        />
      </div>
      
      <div 
        ref={timelineRef}
        className="tw-relative tw-h-16 tw-bg-gray-100 tw-rounded-md tw-cursor-pointer"
        onClick={handleTimelineClick}
        onMouseMove={handleTimelineMouseMove}
      >
        <TimelineRuler duration={duration} streamStart={streamStart} streamEnd={streamEnd} />
        
        <AnimatePresence>
          {previewStartTime !== undefined && previewEndTime !== undefined && (
            <TimelinePreview
              startTime={previewStartTime}
              endTime={previewEndTime}
              duration={duration}
            />
          )}
        </AnimatePresence>

        <TimelineAnnotations
          annotations={annotations}
          duration={duration}
          onAnnotationClick={onAnnotationClick}
          streamStart={streamStart}
        />
        
        <TimelineCursor 
          currentTime={currentTime} 
          duration={duration}
        />
      </div>
      
      <div className="tw-text-sm tw-text-gray-500 tw-text-right tw-font-mono">
        {/* {formatTime(currentTime)} / {formatTime(duration)} */}
        {streamStart && streamEnd && (
          <>
            {formatStreamTime(new Date(streamStart.getTime() + currentTime * 1000))} / {formatStreamTime(streamEnd)}
          </>
        )}
      </div>
    </div>
  );
}
